import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../hooks/useAuth';
import { useFirebase } from '../../../hooks/useFirebase';
import { queryDelete, queryGet, queryPost, queryPut } from '../../tools/tools';

const ENDPOINT = '/promotions';
const KEY = 'promotions-admin';

export const usePromotionEntityQuery = () => {
  const { user } = useAuth();
  const { remoteConfig } = useFirebase();
  const queryClient = useQueryClient();

  const [isMutating, setIsMutating] = useState(false);
  const [mutationError, setMutationError] = useState(null);

  const queryKey = [KEY, user?.uid];

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => queryGet(remoteConfig, ENDPOINT, user?.accessToken),
    enabled: !!user?.accessToken && !!remoteConfig
  });

  const wrapMutate = (mutationFn) => ({ data: payload, onSuccess, onError, onComplete }) => {
    setIsMutating(true);
    setMutationError(null);
    mutationFn(payload, {
      onSuccess: async (res) => {
        onSuccess?.(res);
        onComplete?.(null, res);
        await refetch();
      },
      onError: (err) => {
        setMutationError(err);
        onError?.(err);
        onComplete?.(err, null);
      },
      onSettled: () => setIsMutating(false)
    });
  };

  // EntityTable passes raw form data — normalise it before sending. `alwaysOn` is a form-only
  // toggle, not a field on the entity — when set, it just means the date range is cleared.
  const normalise = (raw) => ({
    ...raw,
    discount: raw.discount !== '' && raw.discount != null ? parseFloat(raw.discount) : null,
    ruleType: raw.ruleType || 'FIRST_ORDER',
    active: raw.active ?? true,
    startsAt: raw.alwaysOn || !raw.startsAt
      ? null
      : raw.startsAt.includes('T') ? raw.startsAt : `${raw.startsAt}T00:00:00`,
    endsAt: raw.alwaysOn || !raw.endsAt
      ? null
      : raw.endsAt.includes('T') ? raw.endsAt : `${raw.endsAt}T23:59:59`,
    minOrderValue: raw.minOrderValue !== '' && raw.minOrderValue != null ? parseFloat(raw.minOrderValue) : null,
    categoryId: raw.categoryId || null,
    usageLimit: raw.usageLimit !== '' && raw.usageLimit != null ? parseInt(raw.usageLimit) : null
  });

  const { mutate: _create, isPending: isCreating, reset: resetCreate } = useMutation({
    mutationFn: (item) => queryPost(remoteConfig, ENDPOINT, normalise(item), user?.accessToken)
  });

  const { mutate: _update, isPending: isUpdating, reset: resetUpdate } = useMutation({
    mutationFn: (item) => queryPut(remoteConfig, ENDPOINT, normalise(item), user?.accessToken)
  });

  const { mutate: _remove, isPending: isDeleting, reset: resetRemove } = useMutation({
    mutationFn: (id) => queryDelete(remoteConfig, ENDPOINT, id, user?.accessToken)
  });

  const reset = () => { resetCreate(); resetUpdate(); resetRemove(); };

  const getById = async (id, onSuccess) => {
    try {
      setIsMutating(true);
      const result = await queryGet(remoteConfig, `${ENDPOINT}/${id}`, user?.accessToken);
      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return [result];
        const idx = old.findIndex((e) => e.id === result.id);
        if (idx >= 0) { const u = [...old]; u[idx] = result; return u; }
        return [...old, result];
      });
      const display = {
        ...result,
        alwaysOn: !result.startsAt && !result.endsAt,
        startsAt: result.startsAt ? result.startsAt.substring(0, 10) : null,
        endsAt: result.endsAt ? result.endsAt.substring(0, 10) : null
      };
      onSuccess?.(display);
      return display;
    } catch (err) {
      setMutationError(err);
      throw err;
    } finally {
      setIsMutating(false);
    }
  };

  return {
    data: data ?? [],
    loading: isLoading,
    error: isError,
    create: wrapMutate(_create),
    update: wrapMutate(_update),
    remove: wrapMutate(_remove),
    getById,
    reset,
    refetch,
    mutating: isCreating || isUpdating || isDeleting || isMutating,
    mutatingError: mutationError
  };
};

export const usePromotionRedemptions = (promotionId) => {
  const { user } = useAuth();
  const { remoteConfig } = useFirebase();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['promotion-redemptions', promotionId, user?.uid],
    queryFn: () => queryGet(remoteConfig, `/promotions/${promotionId}/redemptions`, user?.accessToken),
    enabled: !!promotionId && !!user?.accessToken && !!remoteConfig
  });

  return { redemptions: data ?? [], isLoading, refetch };
};
